import { ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../lib/api";
import { useAccountStatus, useClients, queryKeys, AccountStatus } from "../lib/queries";
import { ConsentForm } from "./ConsentForm";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";

/**
 * Stops a family reaching the app before their account is actually set up, or after it lapses.
 *
 * The hole this closes: signup signs the family in *before* consent and billing, and it has to --
 * `POST /consent` needs an authenticated caller. Everything after that point was enforced only by the
 * signup wizard's local step state, so following the Privacy Policy link and pressing back left them
 * signed in with no consent on file and no subscription, with the full app available.
 *
 * A minor's own login has no account of its own and is deliberately never gated here -- they are not
 * the billpayer, and locking a child out of their training because a guardian's card expired would
 * punish the wrong person. The server refuses `/account/status` for them (no account id), which reads
 * as "not applicable" rather than "blocked".
 */
export function RequireActiveAccount({ children }: { children: ReactNode }) {
  const statusQuery = useAccountStatus();

  if (statusQuery.isPending) return <LoadingState label="Loading your account…" />;

  // Fail open on a failed status read, deliberately. This is an onboarding gate, not a security
  // boundary -- every endpoint behind it enforces its own rules -- and locking a paying family out of
  // their plan because one request failed is the worse error.
  if (statusQuery.isError) return <>{children}</>;

  const status = statusQuery.data;
  // `not_applicable` is a minor's own login. They are deliberately never gated -- they are not the
  // billpayer, and locking a child out of their training because a guardian's card expired would punish
  // the wrong person. This used to arrive as a 403 and be handled by the fail-open branch above, which
  // worked only by accident and left the page looping on an error (UX review F53).
  if (status.access === "ok" || status.access === "not_applicable") return <>{children}</>;

  return <AccountIncomplete status={status} />;
}

/**
 * Consent, given here, in place.
 *
 * This screen used to have no form on it at all. It told people to reopen the invite link their coach
 * emailed them -- and by the time anyone sees this screen the invite has been accepted, so that link
 * always answers "Invite not found. Ask your coach to send a new one", and a resent invite behaves
 * identically. A signed-in, paying account was therefore permanently unable to consent, and the coach
 * could not fix it from their side either (UX review F87).
 *
 * The original comment argued consent belonged only in signup, "where the invite context is known".
 * The part that actually needs invite context is whether to ask for parental consent, and the account's
 * own athletes answer that just as well -- so the gate asks for it whenever there's a minor on the
 * account. The form itself is the same component the wizard uses, so there is still only one wording.
 */
function ConsentRequired() {
  const queryClient = useQueryClient();
  const clients = useClients<Array<{ name: string; is_minor: boolean }>>();
  const minor = clients.data?.find((client) => client.is_minor) ?? null;

  const recordConsent = useMutation({
    mutationFn: (consent: Record<string, boolean>) => apiPost("/consent", consent),
    // Re-reads the gate rather than assuming success let them through: the same status query decides
    // whether billing is still outstanding, which is very often the next thing to deal with.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.accountStatus }),
  });

  return (
    <PageShell title="One more step">
      <p className="form-hint">
        Before you can use your account, we need you to read and accept the Privacy Policy and Terms of
        Service. Your coach can&rsquo;t set up training for you until that&rsquo;s on file.
      </p>

      {recordConsent.isError && <ErrorState error={recordConsent.error} />}

      {/* Waits for the athlete list rather than guessing: asking an adult to consent on behalf of a
          child who doesn't exist, or failing to ask a guardian at all, are both wrong. */}
      {clients.isPending ? (
        <LoadingState label="Loading…" />
      ) : (
        <ConsentForm
          athleteName={minor?.name ?? null}
          submitting={recordConsent.isPending}
          onSubmit={(consent) => recordConsent.mutate(consent)}
          submitLabel="Agree and continue"
        />
      )}
    </PageShell>
  );
}

function AccountIncomplete({ status }: { status: AccountStatus }) {
  const startCheckout = useMutation({
    mutationFn: (planId: string | null) => apiPost<{ url: string }>("/billing/checkout-session", planId ? { planId } : {}),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
  if (status.access === "consent_required") {
    return <ConsentRequired />;
  }

  const lapsed = status.access === "subscription_inactive";

  return (
    <PageShell title={lapsed ? "Your subscription has ended" : "Finish setting up"}>
      <p className="form-hint">
        {lapsed
          ? "Your subscription is no longer active, so training plans and messaging are paused. Restarting picks up exactly where you left off — nothing has been deleted."
          : "Your account is ready apart from payment. Once that's set up, your coach can publish your training plan."}
      </p>

      {startCheckout.isError && <ErrorState error={startCheckout.error} />}

      <button type="button" onClick={() => startCheckout.mutate(null)} disabled={startCheckout.isPending}>
        {startCheckout.isPending ? "Opening…" : lapsed ? "Restart my subscription" : "Set up payment"}
      </button>

      {/* Which plan they come back on is chosen from the coach's catalogue once they are inside again --
          restarting is one decision, and stacking a plan chooser onto a locked-out screen is another. */}
      {lapsed && (
        <p className="form-hint">
          You can pick which plan to come back on once you&rsquo;re back in, including cheaper ones if
          you&rsquo;re not training as much right now.
        </p>
      )}

      {/* Their data is still theirs even while they have no access to the app, so the way out is not a
          dead end. */}
      <p className="form-hint">
        Need to take your data with you instead? Email your coach and they can help.
      </p>
    </PageShell>
  );
}
